import { useState, useEffect, useRef } from "react";
import { useProductGlobalSearch } from "../../queries/useProductsQuery";
import { useDebounce } from "../../hooks/useDebounce";
import { FiCamera } from "react-icons/fi";
import Tesseract from "tesseract.js";
import styles from "./globalsearch.module.css";
import { useNavigate, useLocation } from "react-router-dom";
import { getImageUrl } from "../../lib/get-image";
import { HeadingTitle, SubHeadingTitle, Image } from "../shared";
import { CiSearch } from "react-icons/ci";

const GlobalProductsSearch = ({
  onClose = () => {},
  categories = [],
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const fileInputRef = useRef(null);
  const wrapperRef = useRef(null);

  const [input, setInput] = useState("");
  const [open, setOpen] = useState(false);

  const normalized = input.trim().toLowerCase();

  const debounced = useDebounce(input);

  const shouldSearch = debounced.trim().length >= 2;

  const { data, isPending } = useProductGlobalSearch(
    shouldSearch ? debounced.trim() : ""
  );

  /*
   * Semantic search API response:
   *
   * {
   *   status: "success",
   *   query: "sarees",
   *   corrected_query: "saree",
   *   total_results: 2,
   *   products: [
   *     {
   *       product_id: 153,
   *       similarity: 0.2544,
   *       metadata: {
   *         name: "...",
   *         price: 0,
   *         thumb_image: "...",
   *         slug: "..."
   *       }
   *     }
   *   ]
   * }
   */

  const products = data?.products || [];

  const suggestions =
    normalized.length === 0
      ? categories
      : categories.filter((category) =>
          category?.name?.toLowerCase().includes(normalized)
        );

  const showSuggestions = suggestions.length > 0;
  const showProducts = products.length > 0;

  const isSingleProduct = products.length === 1;

  /* ---------------- RESET SEARCH ---------------- */

  const resetSearch = () => {
    setInput("");
    setOpen(false);
    onClose();
  };

  /* ---------------- IMAGE / OCR SEARCH ---------------- */

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    try {
      const result = await Tesseract.recognize(file, "eng");

      const text = result.data.text;

      console.log("OCR TEXT:", text);

      const extractedItems = text
        .split("\n")
        .map((line) => line.trim())
        .filter((line) => line.length > 2);

      navigate("/shopping-list", {
        state: {
          extractedItems,
        },
      });

      resetSearch();
    } catch (error) {
      console.error("OCR ERROR:", error);
    }

    // Allow selecting the same image again
    e.target.value = "";
  };

  /* ---------------- CATEGORY NAVIGATION ---------------- */

  const goCategory = (slug) => {
    if (!slug) return;

    resetSearch();

    navigate(`/shop?category=${encodeURIComponent(slug)}`);
  };

  /* ---------------- PRODUCT NAVIGATION ---------------- */

  const goProduct = (slug) => {
    if (!slug) return;

    resetSearch();

    navigate(`/productdetails/${slug}`);
  };

  /* ---------------- ENTER KEY SEARCH ---------------- */

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!normalized) return;

    /*
     * If semantic search returned exactly one product,
     * open that product directly.
     */
    if (isSingleProduct) {
      const slug = products[0]?.metadata?.slug;

      if (slug) {
        goProduct(slug);
        return;
      }
    }

    /*
     * Otherwise go to shop search.
     */
    resetSearch();

    navigate(
      `/shop/${encodeURIComponent(
        normalized.replace(/\s+/g, "-")
      )}`
    );
  };

  /* ---------------- CLOSE SEARCH ON ROUTE CHANGE ---------------- */

  useEffect(() => {
    setInput("");
    setOpen(false);
  }, [location.pathname]);

  /* ---------------- CLOSE ON OUTSIDE CLICK ---------------- */

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener(
        "mousedown",
        handleClickOutside
      );
    };
  }, []);

  return (
    <form
      onSubmit={handleSubmit}
      className={styles.searchWrapper}
    >
      <div
        className={styles.searchBox}
        ref={wrapperRef}
      >
        <CiSearch
          className={styles.searchIcon}
          size={22}
        />

        <input
          value={input}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setInput(e.target.value);
            setOpen(true);
          }}
          placeholder="Search for fresh groceries , daily essentials & more..."
          className={styles.searchInput}
        />

        {/* CAMERA BUTTON */}

        {/* 
        <button
          type="button"
          className={styles.cameraBtn}
          onClick={() => fileInputRef.current?.click()}
        >
          <FiCamera size={20} />
        </button>
        */}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={handleImageUpload}
        />

        {open && (
          <div className={styles.searchDropdown}>

            {/* SEARCH LOADING */}

            {shouldSearch && isPending && (
              <SubHeadingTitle subTitle="Searching…" />
            )}

            {/* CATEGORY SUGGESTIONS */}

            {showSuggestions && (
              <>
                <HeadingTitle
                  text="Suggestions"
                  style={styles.dropdownHeading}
                />

                {suggestions.map((item) => (
                  <div
                    key={item.slug}
                    className={styles.dropdownItem}
                    onMouseDown={() =>
                      goCategory(item.slug)
                    }
                  >
                    <CiSearch size={18} />

                    {item.name}
                  </div>
                ))}
              </>
            )}

            {/* SEMANTIC SEARCH PRODUCTS */}

            {showProducts && (
              <>
                <HeadingTitle
                  text="Products"
                  style={styles.dropdownHeading}
                />

                {products.map((product) => {
                  const metadata =
                    product?.metadata || {};

                  return (
                    <div
                      key={product.product_id}
                      className={styles.dropdownItem}
                      onMouseDown={() =>
                        goProduct(metadata.slug)
                      }
                    >
                      <Image
                        src={getImageUrl(
                          metadata.thumb_image
                        )}
                        alt={
                          metadata.name || "Product"
                        }
                        className={
                          styles.itemThumbnail
                        }
                      />

                      <span>
                        {metadata.name}
                      </span>
                    </div>
                  );
                })}
              </>
            )}

            {/* NO RESULTS */}

            {!isPending &&
              shouldSearch &&
              !showSuggestions &&
              !showProducts && (
                <SubHeadingTitle
                  subTitle={`No products found related to "${input}"`}
                />
              )}
          </div>
        )}
      </div>
    </form>
  );
};

export default GlobalProductsSearch;